import React from 'react';
import { Box, Typography, LinearProgress, Card } from '@mui/material';
import { motion } from 'framer-motion';
import {
  SiJavascript,
  SiReact,
  SiTailwindcss,
  SiMui,
  SiPython,
  SiNodedotjs,
  SiC,
  SiCplusplus,
  SiOpenjdk,
  SiFlutter,
  SiFirebase,
  SiGit,
  SiLinux,
  SiAngular,
  SiJirasoftware,
  SiJira,
  SiSqlite,
  SiMongodb,
  SiSap,
  SiHtml5,
  SiCloudflare,
  SiSupabase,
  SiDotnet,
  SiExpress,
  SiNextdotjs,
  SiVuedotjs,
  SiVite,
  SiDjango,
  SiFlask,
  SiFastapi,
  SiDocker,
  SiGraphql,
  SiHelpdesk,
} from 'react-icons/si';
import { MdAdminPanelSettings, MdLightbulb, MdPsychology, MdSelfImprovement } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { staggerContainer, staggerItem, viewportOnce } from '../motion/presets';

const mainSkillsOrder = [
  [
    { name: 'JavaScript', icon: <SiJavascript color="#f7df1e" size={32} />, level: 100 },
    { name: 'React.js', icon: <SiReact color="#61dafb" size={32} />, level: 100 },
    { name: 'Tailwind CSS', icon: <SiTailwindcss color="#38bdf8" size={32} />, level: 100 },
  ],
  [
    { name: 'Material UI', icon: <SiMui color="#007fff" size={32} />, level: 100 },
    { name: 'Python', icon: <SiPython color="#3776AB" size={32} />, level: 100 },
    { name: 'Node.js', icon: <SiNodedotjs color="#339933" size={32} />, level: 100 },
  ],
  [
    { name: 'Cloudflare', icon: <SiCloudflare color="#F38020" size={32} />, level: 55 },
    { name: 'Firebase', icon: <SiFirebase color="#FFCA28" size={32} />, level: 100 },
    { name: 'Supabase', icon: <SiSupabase color="#3FCF8E" size={32} />, level: 80 },
  ],
  [
    { name: 'Linux', icon: <SiLinux color="#FCC624" size={32} />, level: 75 },
    { name: 'SQL', icon: <SiSqlite color="#003B57" size={32} />, level: 90 },
    { name: 'NoSQL', icon: <SiMongodb color="#47A248" size={32} />, level: 100 },
  ],
];

const skillGroups = [
  {
    title: 'Programming',
    skills: [
      'JAVA', 'C programming', 'C++', 'Python', 'JavaScript', 'SQL', 'NoSQL', 'Flutter', 'ReactNative.js', 'Front-End', 'Back-End', 'API',
    ]
  },
  {
    title: 'Frameworks & Tools',
    skills: [
      'React.js', 'Angular', 'Tailwind', 'Material UI', 'Asp.net', 'Node.js', 'Firebase', 'Git', 'Cloudflare', 'Supabase', 'express.js', 'Next.js', 'vue.js', 'vite.js', 'next.js', 'Django', 'Flask', 'FastAPI', 'Docker',
    ]
  },
  {
    title: 'Other',
    skills: [
      'Help Desk', 'SQL', 'NoSQL', 'API', 'Linux', 'Jira Service Management', 'IT Service Management', 'Microsoft admin portal', 'SAP/4HANA', 'Problem Solving', 'Critical Thinking', 'Stress Management'
    ]
  }
];

const iconMap = {
  JAVA: <SiOpenjdk color="#ED8B00" size={20} />,
  'C programming': <SiC color="#A8B9CC" size={20} />,
  'C++': <SiCplusplus color="#00599C" size={20} />,
  Python: <SiPython color="#3776AB" size={20} />,
  JavaScript: <SiJavascript color="#f7df1e" size={20} />,
  SQL: <SiSqlite color="#003B57" size={20} />,
  NoSQL: <SiMongodb color="#47A248" size={20} />,
  Flutter: <SiFlutter color="#02569B" size={20} />,
  'ReactNative.js': <SiReact color="#61dafb" size={20} />,
  'Front-End': <SiHtml5 color="#E34F26" size={20} />,
  'Back-End': <SiNodedotjs color="#339933" size={20} />,
  API: <SiGraphql color="#E10098" size={20} />,
  'React.js': <SiReact color="#61dafb" size={20} />,
  Angular: <SiAngular color="#DD0031" size={20} />,
  Tailwind: <SiTailwindcss color="#38bdf8" size={20} />,
  'Material UI': <SiMui color="#007fff" size={20} />,
  'Asp.net': <SiDotnet color="#512BD4" size={20} />,
  'Node.js': <SiNodedotjs color="#339933" size={20} />,
  Firebase: <SiFirebase color="#FFCA28" size={20} />,
  Git: <SiGit color="#F05032" size={20} />,
  Cloudflare: <SiCloudflare color="#F38020" size={20} />,
  Supabase: <SiSupabase color="#3FCF8E" size={20} />,
  'express.js': <SiExpress color="#000000" size={20} />,
  'Next.js': <SiNextdotjs color="#000000" size={20} />,
  'next.js': <SiNextdotjs color="#000000" size={20} />,
  'vue.js': <SiVuedotjs color="#41B883" size={20} />,
  'vite.js': <SiVite color="#646CFF" size={20} />,
  Django: <SiDjango color="#092E20" size={20} />,
  Flask: <SiFlask color="#000000" size={20} />,
  FastAPI: <SiFastapi color="#009688" size={20} />,
  Docker: <SiDocker color="#2496ED" size={20} />,
  'Help Desk': <SiHelpdesk color="#e95420" size={20} />,
  Linux: <SiLinux color="#FCC624" size={20} />,
  'Jira Service Management': <SiJirasoftware color="#0052CC" size={20} />,
  'IT Service Management': <SiJira color="#0052CC" size={20} />,
  'Microsoft admin portal': <MdAdminPanelSettings color="#0078D4" size={20} />,
  'SAP/4HANA': <SiSap color="#0FAAFF" size={20} />,
  'Problem Solving': <MdLightbulb color="#f6c915" size={20} />,
  'Critical Thinking': <MdPsychology color="#77216f" size={20} />,
  'Stress Management': <MdSelfImprovement color="#25d366" size={20} />,
};

const fontStack = `'Fira Mono', 'Menlo', 'Consolas', 'Liberation Mono', 'monospace'`;

const otherSkillChipSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  fontWeight: 500,
  fontSize: { xs: 11, sm: 12 },
  color: '#fff',
  fontFamily: fontStack,
  background: 'rgba(24, 28, 32, 0.95)',
  border: '1px solid rgba(119, 33, 111, 0.9)',
  borderRadius: '16px',
  height: { xs: 28, sm: 30 },
  px: 1.25,
  lineHeight: 1,
  userSelect: 'none',
  cursor: 'default',
  transition: 'background 0.25s ease, color 0.25s ease, border-color 0.25s ease',
  '&:hover': {
    color: '#f6c915',
    borderColor: '#e95420',
    background: 'linear-gradient(135deg, rgba(233, 84, 32, 0.28), rgba(119, 33, 111, 0.4))',
    boxShadow: '0 10px 26px rgba(233, 84, 32, 0.35)',
  },
};

const panelAlignSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
};

const panelSx = {
  boxSizing: 'border-box',
  width: { xs: '98vw', sm: 800, md: 1000, lg: 1200, xl: 1800, '2xl': 2000 },
  maxWidth: '90vw',
  borderRadius: 3,
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)',
  overflow: 'hidden',
  background: 'rgba(36, 12, 44, 0.98)',
  border: '1.5px solid #77216f',
};

const panelBarSx = {
  display: 'flex',
  alignItems: 'center',
  height: 32,
  px: 2,
  background: 'linear-gradient(90deg, #77216f 60%, #e95420 100%)',
  borderBottom: '1px solid #77216f',
  width: '100%',
  boxSizing: 'border-box',
};

const coreSkillsGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  gap: { xs: 1.5, sm: 2, md: 2.5 },
  width: '100%',
  boxSizing: 'border-box',
};

const skillGroupsGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  gap: { xs: 3, md: 4 },
  width: '100%',
  boxSizing: 'border-box',
  mt: { xs: 3, md: 4 },
  pt: { xs: 3, md: 4 },
  borderTop: '1px solid rgba(119, 33, 111, 0.65)',
};

const coreSkills = mainSkillsOrder.flat();

const Skills = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');

  return (
    <Box
      id="skills"
      sx={{
        py: { xs: 5, sm: 8, xl: 12 },
        width: '100%',
        maxWidth: '100%',
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Box
        component={motion.h2}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          m: 0,
          mb: { xs: 3, sm: 4, md: 5 },
          fontWeight: 700,
          textAlign: 'center',
          color: '#e95420',
          fontSize: { xs: 28, sm: 36, xl: 48 },
          fontFamily: 'Ubuntu, Montserrat, sans-serif',
          direction: isAr ? 'rtl' : 'ltr',
        }}
      >
        {t('skills.title')}
      </Box>

      <Box sx={panelAlignSx}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          sx={panelSx}
        >
          <Box sx={panelBarSx}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e95420', mr: 1 }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f6c915', mr: 1 }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#25d366' }} />
            <Box sx={{ flexGrow: 1 }} />
            <Typography sx={{ color: '#fff', fontFamily: fontStack, fontSize: 14, opacity: 0.75 }}>
              skills — stack
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, fontFamily: fontStack }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, mb: 2 }}>
              {t('skills.coreHint')}
            </Typography>

            <Box
              component={motion.div}
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.12 }}
              sx={coreSkillsGridSx}
            >
              {coreSkills.map((skill) => (
                <Card
                  key={skill.name}
                  component={motion.div}
                  variants={staggerItem}
                  whileHover={{
                    y: -8,
                    scale: 1.03,
                    borderColor: '#e95420',
                    boxShadow: '0 14px 32px rgba(233, 84, 32, 0.28)',
                  }}
                  transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    background: 'rgba(24, 28, 32, 0.92)',
                    border: '1.5px solid rgba(119, 33, 111, 0.85)',
                    color: '#fff',
                    p: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ '& svg': { width: 28, height: 28 }, mb: 1 }}>{skill.icon}</Box>
                  <Typography
                    fontWeight={700}
                    align="center"
                    sx={{
                      color: '#e95420',
                      fontSize: { xs: 13, sm: 14 },
                      mb: 0.5,
                      fontFamily: 'Ubuntu, sans-serif',
                    }}
                  >
                    {skill.name}
                  </Typography>
                  <Typography sx={{ color: '#25d366', fontSize: 11, mb: 1 }}>{skill.level}%</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={skill.level}
                    sx={{
                      width: '100%',
                      height: 8,
                      borderRadius: 5,
                      background: 'rgba(255,255,255,0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        background: 'linear-gradient(90deg, #25d366 0%, #e95420 100%)',
                      },
                    }}
                  />
                </Card>
              ))}
            </Box>

            <Box sx={skillGroupsGridSx}>
              {skillGroups.map((group, groupIdx) => (
                <Box
                  key={group.title}
                  component={motion.div}
                  initial={{ opacity: 0, x: groupIdx % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: groupIdx * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Typography
                    fontWeight={700}
                    sx={{
                      color: '#61afef',
                      mb: 1.5,
                      fontSize: { xs: 15, sm: 16 },
                      fontFamily: fontStack,
                    }}
                  >
                    $ ls {group.title.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-')}
                  </Typography>
                  <Box
                    component={motion.div}
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.15 }}
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                  >
                    {group.skills.map((skill, skillIdx) => {
                      const skillLabel = typeof skill === 'string' ? skill : skill.name;
                      const chipIcon = iconMap[skillLabel];
                      return (
                        <Box
                          key={`${group.title}-${skillLabel}-${skillIdx}`}
                          component={motion.div}
                          variants={staggerItem}
                          whileHover={{
                            y: -5,
                            scale: 1.06,
                          }}
                          whileTap={{ scale: 0.96 }}
                          transition={{ type: 'spring', stiffness: 450, damping: 20 }}
                          sx={otherSkillChipSx}
                        >
                          {chipIcon && (
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', lineHeight: 0 }}>
                              {chipIcon}
                            </Box>
                          )}
                          <Box component="span">{skillLabel}</Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Skills;
